import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Breadcrumb, Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import NavbarGlobal from './NavbarGlobal';
import { obtenerUsuarios } from '../firebase/firebase';
import {
  approvePracticalReadingPendingAccess,
  getPracticalReadingPendingAccessList,
  rejectPracticalReadingPendingAccess,
} from '../services/practicalReadingAccess.service';

const MySwal = withReactContent(Swal);
const LEVEL_OPTIONS = ['Basico', 'Intermedio', 'Avanzado'];

const normalizeText = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const inferSuggestedLevel = (pending) => {
  const candidates = [
    pending?.practicalReadingLevel,
    pending?.practicalReadingModule,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeText(candidate);

    if (normalized.includes('basico')) return 'Basico';
    if (normalized.includes('intermedio')) return 'Intermedio';
    if (normalized.includes('avanzado')) return 'Avanzado';
  }

  return 'Basico';
};

function PracticalReadingPendingAccess() {
  const [loading, setLoading] = useState(true);
  const [pendingItems, setPendingItems] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [error, setError] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedPending, setSelectedPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    displayName: '',
    role: 'student',
    level: 'Basico',
    teacherId: '',
  });
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  const loadPendingItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [pendingList, allUsers] = await Promise.all([
        getPracticalReadingPendingAccessList(),
        obtenerUsuarios(),
      ]);
      setPendingItems(pendingList);
      setTeachers(allUsers.filter((user) => user.teacher === true));
    } catch (loadError) {
      console.error('Error on PracticalReadingPendingAccess.loadPendingItems', loadError);
      setError(loadError?.message || 'No se pudieron cargar los accesos pendientes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingItems();
  }, [loadPendingItems]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists() || userSnap.data()?.isMaster !== true) {
        navigate('/dashboard');
      }
    });

    return () => unsubscribe();
  }, [auth, db, navigate]);

  const pendingCount = pendingItems.length;
  const teacherOptions = useMemo(
    () => teachers.map((teacher) => ({
      id: teacher.id,
      label: teacher.displayName || teacher.name || teacher.email || teacher.id,
    })),
    [teachers]
  );

  const openApproveModal = useCallback((pending) => {
    setSelectedPending(pending);
    setApprovalForm({
      displayName: pending.displayName || pending.email || '',
      role: pending.tecnicaSuggestedRole || 'student',
      level: inferSuggestedLevel(pending),
      teacherId: '',
    });
    setShowApproveModal(true);
  }, []);

  const closeApproveModal = useCallback(() => {
    if (submitting) return;
    setShowApproveModal(false);
    setSelectedPending(null);
  }, [submitting]);

  const handleApprove = useCallback(async (event) => {
    event.preventDefault();

    if (!selectedPending) {
      return;
    }

    try {
      setSubmitting(true);
      await approvePracticalReadingPendingAccess({
        pendingId: selectedPending.id,
        displayName: approvalForm.displayName,
        role: approvalForm.role,
        level: approvalForm.level,
        teacherId: approvalForm.role === 'student' ? approvalForm.teacherId : '',
      });

      await MySwal.fire({
        icon: 'success',
        title: 'Acceso aprobado',
        text: 'La cuenta ya puede ingresar a Técnica con su acceso de Ejercicios Prácticos.',
        confirmButtonText: 'Entendido',
      });

      closeApproveModal();
      await loadPendingItems();
    } catch (approvalError) {
      console.error('Error on PracticalReadingPendingAccess.handleApprove', approvalError);
      await MySwal.fire({
        icon: 'error',
        title: 'No se pudo aprobar',
        text: approvalError?.message || 'No se pudo aprobar el acceso pendiente.',
        confirmButtonText: 'Entendido',
      });
    } finally {
      setSubmitting(false);
    }
  }, [approvalForm, closeApproveModal, loadPendingItems, selectedPending]);

  const handleReject = useCallback(async (pendingId) => {
    const result = await MySwal.fire({
      icon: 'warning',
      title: 'Rechazar solicitud',
      text: 'Esta solicitud quedará cerrada y el usuario tendrá que volver a validarse desde Ejercicios Prácticos.',
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setSubmitting(true);
      await rejectPracticalReadingPendingAccess(pendingId);
      await loadPendingItems();
    } catch (rejectError) {
      console.error('Error on PracticalReadingPendingAccess.handleReject', rejectError);
      await MySwal.fire({
        icon: 'error',
        title: 'No se pudo rechazar',
        text: rejectError?.message || 'No se pudo rechazar la solicitud.',
        confirmButtonText: 'Entendido',
      });
    } finally {
      setSubmitting(false);
    }
  }, [loadPendingItems]);

  return (
    <div className="masterview-page">
      <NavbarGlobal
        title="Pendientes desde Ejercicios Prácticos"
        showBackButton={true}
        customButtons={[
          {
            label: 'Panel master',
            onClick: () => navigate('/master'),
            variant: 'outline-light',
          },
        ]}
      />

      <Container className="py-4">
        <Breadcrumb className="masterview-page__breadcrumb">
          <Breadcrumb.Item href="/dashboard">Inicio</Breadcrumb.Item>
          <Breadcrumb.Item href="/master">Usuarios</Breadcrumb.Item>
          <Breadcrumb.Item active>Pendientes SSO</Breadcrumb.Item>
        </Breadcrumb>

        <section className="master-hero glass mb-4">
          <div className="master-hero__copy">
            <span className="master-hero__eyebrow">Acceso entrante</span>
            <h1>Pendientes desde Ejercicios Prácticos</h1>
            <p>
              Revisa los correos validados en el proyecto externo que aún no existen en Firebase Auth y completa su acceso en Técnica.
            </p>
          </div>

          <div className="master-hero__stats" aria-label="Resumen de pendientes">
            <div className="master-stat master-stat--total">
              <span>{pendingCount}</span>
              <small>Pendientes</small>
            </div>
          </div>
        </section>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="masterview-state glass">
            <Spinner animation="border" variant="primary" />
            <p>Cargando pendientes...</p>
          </div>
        ) : pendingItems.length === 0 ? (
          <div className="masterview-state glass">
            <h2>Sin pendientes</h2>
            <p>No hay solicitudes nuevas desde Ejercicios Prácticos.</p>
          </div>
        ) : (
          <Row className="g-4">
            {pendingItems.map((pending) => (
              <Col xs={12} lg={6} key={pending.id}>
                <Card className="option-card h-100 glass">
                  <Card.Body className="d-flex flex-column gap-3">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <Card.Title className="mb-1">
                          {pending.displayName || pending.email}
                        </Card.Title>
                        <div className="text-muted">{pending.email}</div>
                      </div>
                      <Badge bg="warning" text="dark">
                        Pendiente
                      </Badge>
                    </div>

                    <div className="text-muted">
                      <div>Rol externo: <strong>{pending.practicalReadingRole || 'Sin definir'}</strong></div>
                      <div>Rol sugerido en Técnica: <strong>{pending.tecnicaSuggestedRole || 'student'}</strong></div>
                      <div>Nivel externo: <strong>{pending.practicalReadingLevel || 'Sin referencia'}</strong></div>
                      <div>Módulo externo: <strong>{pending.practicalReadingModule || 'Sin referencia'}</strong></div>
                    </div>

                    <div className="d-flex gap-2 mt-auto">
                      <Button
                        variant="primary"
                        onClick={() => openApproveModal(pending)}
                        disabled={submitting}
                      >
                        Aprobar acceso
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => handleReject(pending.id)}
                        disabled={submitting}
                      >
                        Rechazar
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>

      <Modal show={showApproveModal} onHide={closeApproveModal} centered>
        <Form onSubmit={handleApprove}>
          <Modal.Header closeButton={!submitting}>
            <Modal.Title>Aprobar acceso</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="pendingDisplayName">
              <Form.Label>Nombre visible</Form.Label>
              <Form.Control
                type="text"
                value={approvalForm.displayName}
                onChange={(event) => setApprovalForm((current) => ({
                  ...current,
                  displayName: event.target.value,
                }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="pendingRole">
              <Form.Label>Rol en Técnica</Form.Label>
              <Form.Select
                value={approvalForm.role}
                onChange={(event) => setApprovalForm((current) => ({
                  ...current,
                  role: event.target.value,
                }))}
              >
                <option value="student">Alumno</option>
                <option value="teacher">Profesor</option>
                <option value="master">Master</option>
              </Form.Select>
            </Form.Group>

            {approvalForm.role === 'student' && (
              <>
                <Form.Group className="mb-3" controlId="pendingLevel">
                  <Form.Label>Nivel</Form.Label>
                  <Form.Select
                    value={approvalForm.level}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      level: event.target.value,
                    }))}
                  >
                    {LEVEL_OPTIONS.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="pendingTeacher">
                  <Form.Label>Profesor asignado</Form.Label>
                  <Form.Select
                    value={approvalForm.teacherId}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      teacherId: event.target.value,
                    }))}
                  >
                    <option value="">Sin asignar por ahora</option>
                    {teacherOptions.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>{teacher.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-primary" onClick={closeApproveModal} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Aprobando...' : 'Crear acceso'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default PracticalReadingPendingAccess;
